/**
 * Undoable Einzel-Mutationen der Akte für den „still + Undo"-Flow (B1 Drag-to-Papierkorb, B2
 * Cross-Category-Reclassify): führt die Vorwärts-Op optimistisch aus und zeigt statt eines reinen
 * Erfolgs-Toasts einen „Rückgängig"-Toast, der die Inverse puffert. Inversen: Delete→Restore,
 * Move→zurück in den Ursprungsordner, Reclassify→zurück auf ursprünglichen Kind + Datum. Ein
 * `invalidate()` nach Vorwärts- wie Rücknahme-Op — der Refetch ist die Wahrheit.
 */
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type PropertyDocumentKind,
  type PropertyFileResponse,
  type PropertyFileTreeNode,
} from '@/shared/api';
import { akteErrorMessage } from './akteErrorMessages';
import { segmentsOf } from './akteFolderRules';
import { PROPERTY_DOCUMENT_KIND_SHORT } from './akteKindLabels';
import { invalidatePropertyFiles } from './akteTreeCache';
import { moveFileInTree, removeFileFromTree } from './akteTreeOps';
import { showUndoToast } from './akteUndoToast';

function folderName(path: string): string {
  return segmentsOf(path).pop() ?? path;
}

interface Undoable {
  /** Optionales optimistisches Tree-Update (Move/Delete); Reclassify leitet das Backend ab. */
  transform?: (tree: PropertyFileTreeNode) => PropertyFileTreeNode;
  forward: () => Promise<unknown>;
  message: string;
  undo: () => Promise<unknown>;
  undoneMessage: string;
}

export function useAkteUndoableActions(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const treeKey = ['propertyFiles', 'tree', propertyId] as const;
  const invalidate = () => invalidatePropertyFiles(qc, propertyId);

  async function perform({ transform, forward, message, undo, undoneMessage }: Undoable) {
    let prev: PropertyFileTreeNode | undefined;
    if (transform) {
      await qc.cancelQueries({ queryKey: treeKey });
      prev = qc.getQueryData<PropertyFileTreeNode>(treeKey);
      if (prev) qc.setQueryData(treeKey, transform(prev));
    }
    try {
      await forward();
      invalidate();
      showUndoToast(
        message,
        async () => {
          await undo();
          invalidate();
        },
        { undoneMessage },
      );
    } catch (err) {
      if (prev) qc.setQueryData(treeKey, prev);
      toast.error(akteErrorMessage(await toApiError(err)));
    }
  }

  return {
    /** Soft-Delete mit „Rückgängig" → Restore. */
    deleteWithUndo: (file: PropertyFileResponse) =>
      perform({
        transform: (t) => removeFileFromTree(t, file.id),
        forward: () => api.propertyFiles.delete(propertyId, file.id),
        message: 'In den Papierkorb verschoben.',
        undo: () => api.propertyFiles.restore(propertyId, file.id),
        undoneMessage: 'Wiederhergestellt.',
      }),

    /** Move in `targetFolder` mit „Rückgängig" → zurück in den Ursprungsordner. */
    moveWithUndo: (file: PropertyFileResponse, targetFolder: string) =>
      perform({
        transform: (t) => moveFileInTree(t, file.id, targetFolder),
        forward: () => api.propertyFiles.move(propertyId, file.id, { targetFolder }),
        message: `Nach ${folderName(targetFolder)} verschoben.`,
        undo: () => api.propertyFiles.move(propertyId, file.id, { targetFolder: file.folder }),
        undoneMessage: 'Verschieben rückgängig gemacht.',
      }),

    /** Reclassify auf `kind` mit „Rückgängig" → zurück auf ursprünglichen Kind + Datum. */
    reclassifyWithUndo: (
      file: PropertyFileResponse,
      kind: PropertyDocumentKind,
      issuedOn?: string | null,
    ) =>
      perform({
        forward: () => api.propertyFiles.reclassify(propertyId, file.id, { kind, issuedOn }),
        message: `Als ${PROPERTY_DOCUMENT_KIND_SHORT[kind]} neu einsortiert.`,
        undo: () =>
          api.propertyFiles.reclassify(propertyId, file.id, {
            kind: file.kind,
            issuedOn: file.issuedOn,
          }),
        undoneMessage: 'Umklassifizieren rückgängig gemacht.',
      }),
  };
}
